import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ArticleCard from '@/components/feature/ArticleCard';
import { articles, categories } from '@/mocks/siteData';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find(a => a.slug === slug);

  if (!article) {
    return (
      <main>
        <Navbar />
        <div className="pt-20 min-h-screen flex items-center justify-center px-4">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground-950 mb-3">文章未找到</h1>
            <Link to="/" className="text-sm text-primary-500 hover:text-primary-600 cursor-pointer">返回首页</Link>
          </div>
        </div>
        <Footer />
      </main>
    );
  }

  const category = categories.find(c => c.slug === article.category);
  const relatedArticles = articles
    .filter(a => a.category === article.category && a.slug !== article.slug)
    .slice(0, 3);

  const catName = category?.name || article.category;

  return (
    <>
      <ScrollToTop />
      <main>
        <Navbar />

        {/* Article Header */}
        <section className="pt-24 pb-8 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <Breadcrumb items={[
              { label: '首页', to: '/' },
              { label: catName, to: `/category/${article.category}` },
              { label: article.title },
            ]} />

            <div className="mt-6">
              <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-primary-100 text-primary-700 mb-3">
                {catName}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-foreground-950 leading-tight">
                {article.title}
              </h1>
              <p className="text-base text-foreground-600 mt-3 leading-relaxed">{article.excerpt}</p>
              <div className="flex items-center gap-4 mt-4 text-xs text-foreground-400">
                <time dateTime={article.publishedAt}>发布于 {article.publishedAt}</time>
                {article.updatedAt !== article.publishedAt && (
                  <time dateTime={article.updatedAt}>更新于 {article.updatedAt}</time>
                )}
                <span>{article.readTime} 分钟阅读</span>
              </div>
            </div>
          </div>
        </section>

        {/* Featured Image */}
        <section className="px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div className="relative w-full aspect-[16/9] rounded-lg overflow-hidden">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover"
                title={article.title}
              />
            </div>
          </div>
        </section>

        {/* Article Content */}
        <article className="py-10 px-4 md:px-6 bg-background-50">
          <div className="max-w-3xl mx-auto">
            <div
              className="prose-custom article-content"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            {/* Tags */}
            <div className="mt-10 pt-6 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                {article.tags.map(tag => (
                  <span key={tag} className="text-xs px-3 py-1 rounded-full bg-secondary-100 text-secondary-700 whitespace-nowrap">
                    {tag}
                  </span>
                ))}
              </div>
            </div>

            {/* Schema.org Article */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                "headline": article.title,
                "description": article.excerpt,
                "datePublished": article.publishedAt,
                "dateModified": article.updatedAt,
                "author": {
                  "@type": "Organization",
                  "name": "体位指南编辑部"
                },
                "publisher": {
                  "@type": "Organization",
                  "name": "体位指南",
                  "url": "https://position-guide.com/"
                },
                "mainEntityOfPage": {
                  "@type": "WebPage",
                  "@id": `https://position-guide.com/article/${article.slug}`
                },
                "keywords": article.tags.join(',')
              })}
            </script>

            {/* BreadcrumbList Schema */}
            <script type="application/ld+json">
              {JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "position": 1,
                    "name": "首页",
                    "item": "https://position-guide.com/"
                  },
                  {
                    "@type": "ListItem",
                    "position": 2,
                    "name": catName,
                    "item": `https://position-guide.com/category/${article.category}`
                  },
                  {
                    "@type": "ListItem",
                    "position": 3,
                    "name": article.title,
                    "item": `https://position-guide.com/article/${article.slug}`
                  }
                ]
              })}
            </script>
          </div>
        </article>

        {/* Related Articles */}
        {relatedArticles.length > 0 && (
          <section className="py-12 px-4 md:px-6 bg-background-100">
            <div className="max-w-3xl mx-auto">
              <h3 className="text-xl font-bold font-heading text-foreground-950 mb-5">相关文章</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map(ra => (
                  <ArticleCard key={ra.slug} {...ra} />
                ))}
              </div>
            </div>
          </section>
        )}

        <Footer />
      </main>
    </>
  );
}